import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Moon, Sun, Calendar, Tag, Smartphone, User, ChevronRight, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useTheme } from "@/context/ThemeContext";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { track, EVENT } from "@/lib/track";
import { useLogo } from "@/lib/useLogo";
import { getPageSections } from "@/config/pageSectionNav";

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "App", to: "/#home-app" },
  { label: "About", to: "/about" },
  { label: "Locations", to: "/locations" },
  { label: "Training", to: "/services" },
  { label: "Packages", to: "/packages" },
  { label: "Booking", to: "/booking" },
  { label: "Mavs", to: "/mavs-volleyball" },
  { label: "Offers", to: "/offers" },
  { label: "Events", to: "/events" },
  { label: "News", to: "/news" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "FAQ", to: "/faq" },
  { label: "Support", to: "/support" },
  { label: "Contact / App", to: "/contact" },
  { label: "Live Video Join", to: "/live" },
  { label: "Login / Portal", to: "/login" },
];

const DESKTOP_PRIMARY = NAV_LINKS.slice(0, 8);

// Mobile nav row: page link + optional collapsible list of in-page sections.
const MobileNavItem = ({ link, onNavigate }) => {
  const sections = getPageSections(link.to);
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`mobile-nav-item-${link.to === "/" ? "home" : link.to.replace(/\//g, "")}`}>
      <div className="flex items-center">
        <NavLink
          to={link.to}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            "flex-1 rounded-md px-3 py-3 font-condensed text-base font-medium uppercase tracking-wide transition-colors",
            isActive ? "bg-accent text-brand" : "text-foreground hover:bg-accent"
          )}
        >
          {link.label}
        </NavLink>
        {sections.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={`Toggle ${link.label} sections`}
            data-testid={`mobile-nav-toggle-${link.to === "/" ? "home" : link.to.replace(/\//g, "")}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground/60 hover:bg-accent transition-colors"
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")} />
          </button>
        ) : (
          <ChevronRight className="mr-4 h-4 w-4 shrink-0 opacity-30" />
        )}
      </div>
      {open && sections.length > 0 && (
        <div className="mb-1 ml-3 flex flex-col border-l border-border pl-2">
          {sections.map((s) => (
            <Link
              key={s.id}
              to={`${link.to}#${s.id}`}
              onClick={onNavigate}
              data-testid={`mobnavsub-${s.id}`}
              className="rounded-md px-3 py-2 font-condensed text-sm font-medium tracking-wide text-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
            >
              {s.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSite();
  const logo = useLogo();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "sticky top-0 z-50 w-full border-b backdrop-blur-md transition-colors duration-200 bg-background/70",
        scrolled ? "border-border" : "border-transparent"
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" data-testid="site-logo">
          <img src={logo} alt={settings.brand_name} className="h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {DESKTOP_PRIMARY.map((l) => {
            const sections = getPageSections(l.to);
            return (
              <div key={l.to} className="relative group" data-testid={`nav-item-${l.to === "/" ? "home" : l.to.replace(/\//g, "")}`}>
                <NavLink
                  to={l.to}
                  className={({ isActive }) => cn(
                    "flex items-center gap-1 px-3 py-2 font-condensed text-sm font-medium uppercase tracking-wide transition-colors",
                    isActive ? "text-brand" : "text-foreground/80 hover:text-foreground"
                  )}
                >
                  {l.label}
                  {sections.length > 0 && (
                    <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform duration-200 group-hover:rotate-180" />
                  )}
                </NavLink>
                {sections.length > 0 && (
                  <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="min-w-[240px] overflow-hidden rounded-xl border border-border bg-background/95 p-1.5 shadow-xl backdrop-blur-md" data-testid={`nav-dropdown-${l.to === "/" ? "home" : l.to.replace(/\//g, "")}`}>
                      {sections.map((s) => (
                        <Link
                          key={s.id}
                          to={`${l.to}#${s.id}`}
                          data-testid={`navsub-${s.id}`}
                          className="block rounded-md px-3 py-2 font-condensed text-sm font-medium tracking-wide text-foreground/75 transition-colors hover:bg-accent hover:text-foreground"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="site-header-theme-toggle"
            aria-label="Toggle theme"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-foreground hover:bg-accent transition-colors"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <a
            href={settings.exercise_booking_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="site-header-book-cta"
            onClick={() => track(EVENT.BOOKING_CLICK, "Header Book Intro")}
            className="hidden sm:inline-flex items-center rounded-md bg-brand px-4 py-2.5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"
          >
            Book Intro
          </a>

          <Link
            to="/login"
            data-testid="site-header-login-cta"
            onClick={() => track(EVENT.LOGIN_CLICK, user ? "Header Portal" : "Header Login")}
            className="hidden md:inline-flex items-center rounded-md border border-border px-4 py-2.5 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"
          >
            {user ? "Portal" : "Login"}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="site-header-menu-button"
                aria-label="Open menu"
                className="flex h-10 w-10 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto p-0">
              <div className="flex items-center gap-2 border-b border-border p-4">
                <img src={logo} alt={settings.brand_name} className="h-8 w-auto" />
              </div>
              <nav className="flex flex-col p-2" data-testid="mobile-nav">
                {NAV_LINKS.map((l) => (
                  <MobileNavItem key={l.to} link={l} onNavigate={() => setOpen(false)} />
                ))}
              </nav>
              <div className="grid grid-cols-2 gap-2 p-4">
                <a href={settings.exercise_booking_url} target="_blank" rel="noopener noreferrer" onClick={() => track(EVENT.BOOKING_CLICK, "Mobile Book Intro")} className="col-span-2 inline-flex items-center justify-center rounded-md bg-brand px-4 py-3 text-sm font-semibold uppercase text-white">Book Intro Workout</a>
                <a href={settings.app_store_url} target="_blank" rel="noopener noreferrer" onClick={() => track(EVENT.APP_DOWNLOAD_CLICK, "Mobile App Store")} className="inline-flex items-center justify-center rounded-md border border-border px-4 py-3 text-sm font-semibold uppercase">App Store</a>
                <a href={settings.google_play_url} target="_blank" rel="noopener noreferrer" onClick={() => track(EVENT.APP_DOWNLOAD_CLICK, "Mobile Google Play")} className="inline-flex items-center justify-center rounded-md border border-border px-4 py-3 text-sm font-semibold uppercase">Google Play</a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
